import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WritableDraft } from "immer";
import { update } from "lodash-es";

import { intitalFilterState } from "./AnalyticsFilterReducer";
import { AnalyticsV2Keys, IAnalyticsV2Filters } from "../abstractions";
import { AnalyticsV2WidgetLoadingState, IWidgetBase } from "../abstractions/IAnalytics";
import { IAnalyticsV2APIResponse } from "../abstractions/IAnalyticsAPI";


interface ILoadingDataPayload {
  key: keyof IAnalyticsWidgetState
}

interface IRefreshWidgetPayload {
  key: keyof IAnalyticsWidgetState,
  filter: IAnalyticsV2Filters
}

export interface IWidgetLastUpdatedDateTime extends IWidgetBase {
  widgetData?: string;
}

export interface IAnalyticsWidgetState {
  [AnalyticsV2Keys.UsageSummary]: IWidgetBase;
  [AnalyticsV2Keys.LastUpdated]: IWidgetLastUpdatedDateTime;
  [AnalyticsV2Keys.UsageByTimePeriod]: IWidgetBase;
  [AnalyticsV2Keys.UsageByRetentionIntensity]: IWidgetBase;
  [AnalyticsV2Keys.UsageByRegion]: IWidgetBase;
  [AnalyticsV2Keys.UsageByHost]: IWidgetBase
  [AnalyticsV2Keys.UsageByOS]: IWidgetBase;
  [AnalyticsV2Keys.UsageByPlatform]: IWidgetBase;
  [AnalyticsV2Keys.UsageByCapability]: IWidgetBase;
}

const widgetEmpty = {
  status: AnalyticsV2WidgetLoadingState.NotLoaded,
  filter: intitalFilterState,
}

const initialState: IAnalyticsWidgetState = {
  [AnalyticsV2Keys.UsageSummary]: widgetEmpty,
  [AnalyticsV2Keys.LastUpdated]: widgetEmpty,
  [AnalyticsV2Keys.UsageByTimePeriod]: widgetEmpty,
  [AnalyticsV2Keys.UsageByRetentionIntensity]: widgetEmpty,
  [AnalyticsV2Keys.UsageByRegion]: widgetEmpty,
  [AnalyticsV2Keys.UsageByHost]: widgetEmpty,
  [AnalyticsV2Keys.UsageByOS]: widgetEmpty,
  [AnalyticsV2Keys.UsageByPlatform]: widgetEmpty,
  [AnalyticsV2Keys.UsageByCapability]: widgetEmpty,
};

export const analyticsWidgetsSlice = createSlice({
  name: "analyticsWidgets",
  initialState,
  reducers: {
    resetWidgets: () => initialState,
    refreshWidget: (state, action: PayloadAction<IRefreshWidgetPayload>) => {
      update(state, [action.payload.key], () => (
        {
          status: AnalyticsV2WidgetLoadingState.Refreshing,
          filter: action.payload.filter,
        }
      ));
    },
    loadingData: (state, { payload }: PayloadAction<ILoadingDataPayload>) => {
      update(state, [payload.key], () => (
        {
          ...state[payload.key],
          status: AnalyticsV2WidgetLoadingState.Loading,
        }
      ));
    },
    loadingFailed: (state, { payload }: PayloadAction<ILoadingDataPayload>) => {
      update(state, [payload.key], () => (
        {
          ...state[payload.key],
          status: AnalyticsV2WidgetLoadingState.Error,
        }
      ));
    },
    loadingSuccess: (state, { payload }: PayloadAction<ILoadingDataPayload>) => {
      update(state, [payload.key], () => (
        {
          ...state[payload.key],
          status: AnalyticsV2WidgetLoadingState.Loaded,
        }
      ));
    },
    setLastUpdated: (state, { payload }: PayloadAction<{
      apiResponse: IAnalyticsV2APIResponse,
    }>) => {
      setLastUpdated(state, payload.apiResponse.latestUsageDate);
    },
  }
});


const setLastUpdated = (state: WritableDraft<IAnalyticsWidgetState>, lastUpdated: string) => {
  if (state[AnalyticsV2Keys.LastUpdated].widgetData !== lastUpdated) {
    state[AnalyticsV2Keys.LastUpdated].status = AnalyticsV2WidgetLoadingState.Loaded;
    state[AnalyticsV2Keys.LastUpdated].widgetData = lastUpdated;
  }
}
